import { useEffect, useMemo } from "react";
import { Link } from "@/lib/router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentDashboardsApi } from "../api/agentDashboards";
import { agentsApi } from "../api/agents";
import { metricAlertsApi } from "../api/metricAlerts";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useConfirm } from "../context/ConfirmContext";
import { queryKeys } from "../lib/queryKeys";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/PageSkeleton";
import { Identity } from "../components/Identity";
import { cn } from "../lib/utils";
import { AlertTriangle, BarChart3, Check, Trash2, Radar, Activity, Calendar, Stethoscope, Building2 } from "lucide-react";
import { timeAgo } from "../lib/timeAgo";
import type { AvailableMetric } from "../api/metricAlerts";

export function AgentDashboards() {
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  useEffect(() => {
    setBreadcrumbs([{ label: "Analytics" }]);
  }, [setBreadcrumbs]);

  const { data: dashboards, isLoading } = useQuery({
    queryKey: queryKeys.agentDashboards.list(selectedPracticeId!),
    queryFn: () => agentDashboardsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: agents } = useQuery({
    queryKey: queryKeys.agents.list(selectedPracticeId!),
    queryFn: () => agentsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: metricAlerts } = useQuery({
    queryKey: ["metric-alerts", selectedPracticeId],
    queryFn: () => metricAlertsApi.list(selectedPracticeId!, { acknowledged: false, limit: 20 }),
    enabled: !!selectedPracticeId,
  });

  const { data: availableMetrics } = useQuery({
    queryKey: ["available-metrics", selectedPracticeId],
    queryFn: () => metricAlertsApi.availableMetrics(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (alertId: string) => metricAlertsApi.acknowledge(selectedPracticeId!, alertId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["metric-alerts", selectedPracticeId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => agentDashboardsApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentDashboards.list(selectedPracticeId!),
      });
    },
  });

  const agentMap = useMemo(
    () => new Map(agents?.map((a) => [a.id, a]) ?? []),
    [agents],
  );

  if (!selectedPracticeId) {
    return <EmptyState icon={BarChart3} message="Select a practice." />;
  }

  if (isLoading) {
    return <PageSkeleton variant="list" />;
  }

  return (
    <div className="space-y-6">
      {/* Metric Alerts */}
      {metricAlerts && metricAlerts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Active Alerts ({metricAlerts.length})
          </h3>
          <div className="border border-border divide-y divide-border overflow-hidden">
            {metricAlerts.map((alert) => {
              const severityIcon: Record<string, string> = {
                critical: "text-red-600 dark:text-red-400",
                warning: "text-amber-600 dark:text-amber-400",
                info: "text-blue-600 dark:text-blue-400",
              };
              return (
                <div key={alert.id} className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30 transition-colors">
                  <AlertTriangle className={cn("h-4 w-4 shrink-0", severityIcon[alert.severity] ?? severityIcon.info)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{alert.thresholdLabel}</span>
                      <span className={cn(
                        "text-[10px] font-medium px-1.5 py-0.5 rounded-sm uppercase",
                        alert.severity === "critical"
                          ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          : alert.severity === "warning"
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                      )}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{alert.message}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{timeAgo(alert.createdAt)}</span>
                  <button
                    type="button"
                    title="Acknowledge"
                    className="p-1.5 rounded-md text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors shrink-0"
                    onClick={() => acknowledgeMutation.mutate(alert.id)}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available LookML Metrics */}
      {availableMetrics && availableMetrics.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Available LookML Metrics ({availableMetrics.length})
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(() => {
              const categoryIcons: Record<string, typeof Radar> = {
                "Revenue Cycle": Activity,
                Scheduling: Calendar,
                Clinical: Stethoscope,
                Operations: Building2,
              };
              const categoryColors: Record<string, string> = {
                "Revenue Cycle": "text-emerald-600 bg-emerald-500/10",
                Scheduling: "text-blue-600 bg-blue-500/10",
                Clinical: "text-purple-600 bg-purple-500/10",
                Operations: "text-amber-600 bg-amber-500/10",
              };
              return availableMetrics.map((m: AvailableMetric) => {
                const Icon = categoryIcons[m.category] ?? Radar;
                const colorClass = categoryColors[m.category] ?? "text-muted-foreground bg-muted";
                const [iconColor, iconBg] = colorClass.split(" ");
                return (
                  <div
                    key={m.key}
                    className="border border-border rounded-lg p-4 hover:bg-accent/30 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn("rounded-md p-2 shrink-0", iconBg)}>
                        <Icon className={cn("h-4 w-4", iconColor)} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold">{m.label}</h4>
                        </div>
                        <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground mt-1">
                          {m.category}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1.5">{m.description}</p>
                        <div className="mt-2 text-[11px] text-muted-foreground font-mono bg-muted/50 rounded px-2 py-1.5">
                          <div>{m.query.model} → {m.query.explore}</div>
                          <div className="truncate">{m.measureField}</div>
                          <div className="mt-0.5 text-foreground/70">
                            Default: {m.defaultOperator} {m.defaultValue.toLocaleString()} ({m.defaultSeverity})
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Dashboards */}
      {!dashboards || dashboards.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          message="No analytics dashboards yet. Agents will create them during their runs."
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {dashboards.map((d) => {
            const owner = d.agentId ? agentMap.get(d.agentId) : null;
            return (
              <div
                key={d.id}
                className="relative border border-border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <Link
                  to={`/analytics/${d.id}`}
                  className="no-underline text-inherit block p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-blue-500/10 p-2 shrink-0">
                      <BarChart3 className="h-4 w-4 text-blue-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold truncate">{d.title}</h3>
                      {d.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {d.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-3">
                        {owner && <Identity name={owner.name} size="sm" />}
                        <span className="text-xs text-muted-foreground">
                          {timeAgo(d.updatedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="Delete dashboard"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (
                      await confirm({
                        title: `Delete "${d.title}"?`,
                        description: "This cannot be undone.",
                        destructive: true,
                      })
                    ) {
                      deleteMutation.mutate(d.id);
                    }
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
